import React, { createContext, useState, useContext, useCallback } from "react";

import {
  getDocs,
  query,
  collection,
  orderBy,
  limit,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "firebaseConfig";
import { QuotationFireStoreFields } from "types/canvasTypes";

const CACHE_KEY = "quotations_cache";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

type CachePayload = {
  timestamp: number;
  data: QuotationFireStoreFields[];
};

function readCache(): QuotationFireStoreFields[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed: CachePayload = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCache(data: QuotationFireStoreFields[]): void {
  try {
    const payload: CachePayload = { timestamp: Date.now(), data };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage full or unavailable — silent fail
  }
}

interface Props {
  children: React.ReactNode;
}

interface QuotationListContextType {
  getQuotations: (forceRefresh?: boolean) => Promise<void>;
  loading: boolean;
  quotations: QuotationFireStoreFields[];
  deleteQuotation: (id: string, name: string, email: string) => void;
}

export const QuotationListContext = createContext<QuotationListContextType>({
  getQuotations: () => Promise.resolve(),
  loading: false,
  quotations: [],
  deleteQuotation: () => {},
});

export const QuotationListContextProvider = ({ children }: Props) => {
  const [quotations, setQuotationList] = useState<QuotationFireStoreFields[]>(
    () => readCache() ?? []
  );
  const [loading, setLoading] = useState(() => readCache() === null);

  const fetchFromFirestore = useCallback(async (): Promise<QuotationFireStoreFields[]> => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(
        query(collection(db, "quotations"), orderBy("date", "desc"), limit(300))
      );
      const data = querySnapshot.docs.map((d) => {
        const docData = d.data() as QuotationFireStoreFields;
        return { ...docData, id: d.id };
      });
      writeCache(data);
      return data;
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getQuotations = useCallback(async (
    forceRefresh: boolean = false
  ): Promise<void> => {
    if (!forceRefresh) {
      const cached = readCache();
      if (cached) {
        setQuotationList(cached);
        setLoading(false);
        return;
      }
    }
    const data = await fetchFromFirestore();
    setQuotationList(data);
  }, [fetchFromFirestore]);

  const deleteQuotation = async (id: string, name: string, email: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this quotation for " +
        name +
        " (" +
        email +
        ")?"
    );
    if (confirmDelete) {
      try {
        await deleteDoc(doc(db, "quotations", id));
        const updated = quotations.filter((quotation) => quotation.id !== id);
        setQuotationList(updated);
        writeCache(updated);
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <QuotationListContext.Provider
      value={{ getQuotations, loading, quotations, deleteQuotation }}
    >
      {children}
    </QuotationListContext.Provider>
  );
};

export const useFetchQuotationContext = () => useContext(QuotationListContext);
